import { useQueries, useQuery } from "@tanstack/react-query";

import { calendarService } from "../../planner/api";
import type { CalendarEvent } from "../../planner/api/types/calendar";

/**
 * Cross-module dependency: the call homepage's "Today's Schedule" panel mirrors
 * what the Planner shows for today, filtered to events that have a video_link.
 * The Planner already owns the calendar API client (auth, URL prefix, types),
 * so we reuse it rather than duplicating that wiring in the call module.
 */
export interface UseTodaysVideoEventsResult {
  events: CalendarEvent[];
  isLoading: boolean;
  hasError: boolean;
}

function todayBoundsISO(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  return { start: start.toISOString(), end: end.toISOString() };
}

export function useTodaysVideoEvents(): UseTodaysVideoEventsResult {
  const calendarsQuery = useQuery({
    queryKey: ["calendar", "calendars"],
    queryFn: () => calendarService.getCalendars(),
  });

  const { start, end } = todayBoundsISO();
  const calendarIds = calendarsQuery.data?.results.map((c) => c.id) ?? [];

  const eventQueries = useQueries({
    queries: calendarIds.map((calendarId) => ({
      queryKey: ["calendar", "events", calendarId, start, end],
      queryFn: () => calendarService.getEvents(calendarId, { start, end }),
    })),
  });

  const now = Date.now();
  const events = eventQueries
    .flatMap((q) => q.data?.results ?? [])
    .filter((e) => !!e.video_link)
    .filter((e) => new Date(e.end).getTime() > now)
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  const isLoading =
    calendarsQuery.isLoading ||
    (calendarIds.length > 0 && eventQueries.some((q) => q.isLoading));
  const hasError = calendarsQuery.isError || eventQueries.some((q) => q.isError);

  return { events, isLoading, hasError };
}
